import ApiClient from 'api-client/ApiClient'
import { useAppSelector } from 'app/store'
import { clientActions } from 'core/client/clientReducers'
import DocumentMeta from 'core/components/DocumentMeta'
import OverviewActions from 'core/elements/overview/OverviewActions'
import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { routeManager } from 'core/utils/routes'
import jsYaml from 'js-yaml'
import { K8sPluginGlobalPrefs } from 'k8s/model'
import { isEmpty } from 'ramda'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import useReactRouter from 'use-react-router'
import { pathStrOr } from 'utils/fp'
import EntityYamlPage from '../../../common/entity/entity-yaml-page'
import { listPersistentVolumes, updatePersistentVolume } from '../actions'
import DeletePersistentVolumeDialog from '../DeletePersistentVolumeDialog'
import { IPersistentVolumeDetailsPageTabs } from '../model'
import { persistentVolumeSelector } from '../selectors'
import Overview from './Overview'

const { capi } = ApiClient.getInstance()

const actions = {
  rightActions: [
    {
      label: 'Delete',
      icon: 'trash-alt',
      DialogComponent: DeletePersistentVolumeDialog,
    },
  ],
}

export default function PersistentVolumeDetailsPage() {
  const dispatch = useDispatch()
  const { match } = useReactRouter()
  const { id } = match.params
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences<K8sPluginGlobalPrefs>(
    'k8sPluginGlobalParams',
  )
  const clusterId = k8sPluginGlobalPerfs?.clusterId || ''
  const { loading } = useListAction(listPersistentVolumes, { params: { clusterId } })
  const persistentVolumes = useAppSelector(persistentVolumeSelector)
  const persistentVolume = useMemo(() => persistentVolumes.find((plugin) => plugin.id === id), [
    id,
    persistentVolumes,
  ])
  const { update, updating } = useUpdateAction(updatePersistentVolume)

  useEffect(() => {
    dispatch(
      clientActions.updateBreadcrumbParams({
        clusterId: persistentVolume?.clusterName || clusterId,
        id: persistentVolume?.name || id,
      }),
    )
    return () => {
      dispatch(clientActions.resetBreadcrumbParams())
    }
  }, [persistentVolume?.clusterName, persistentVolume?.name, id, clusterId])

  const getPersistentVolumeYaml = useCallback(async () => {
    if (isEmpty(persistentVolume)) return undefined
    return capi.getPersistentVolume(persistentVolume.name)
  }, [persistentVolume])

  const handlePersistentVolumeUpdate = async (yaml) => {
    const body = jsYaml.load(yaml)
    const namespace = pathStrOr('', 'metadata.namespace', body)
    const name = pathStrOr('', 'metadata.name', body)
    await update({
      id,
      clusterId,
      namespace,
      name,
      body,
    })
  }

  return (
    <>
      <DocumentMeta title="Data Volume" breadcrumbs />
      <div data-testid="persistent-volume-actions-panel">
        <OverviewActions actions={actions} entity={persistentVolume} />
      </div>
      <Tabs
        route={routeManager.routes.kubernetes.resources.persistentVolumes.details}
        data-testid="persistent-volume-detail-tabs"
      >
        <Tab
          label="Overview"
          value={IPersistentVolumeDetailsPageTabs.Overview}
          data-testid="persistent-volume-overview-tab"
        >
          <Overview persistentVolume={persistentVolume} loading={loading} />
        </Tab>
        <Tab
          label="YAML"
          value={IPersistentVolumeDetailsPageTabs.Yaml}
          data-testid="persistent-volume-yaml-tab"
        >
          <EntityYamlPage
            entityType="Persistent Volume"
            entityName={persistentVolume?.name}
            getYamlFn={getPersistentVolumeYaml}
            handleUpdate={handlePersistentVolumeUpdate}
            loading={loading || updating}
          />
        </Tab>
      </Tabs>
    </>
  )
}
